import { Spinner } from 'rcd-cpp-ui/ui/loader';
import * as React from 'react';
import './infiniteLoader.scss';
import { cn, debounce } from '../../utils';
import { useSelector } from 'react-redux';

interface Props {
  loadData?: (cb?: Function) => any;
  handleScroll?: () => void;
  loading?: boolean;
  horizontal?: boolean;
  container: any;
  threshold?: number; // 0 - 1
  forceResize?: boolean;
}

const InfiniteLoader = (p: Props) => {
  const [loading] = useInfiniteLoader(p);

  return (
    <div className={cn({ 'infinite-loading': loading }, 'infinite-loader')}>
      <span>
        <Spinner spinnerSize="icon" />
        Loading...
      </span>
    </div>
  );
};

export const useInfiniteLoader = ({
  container,
  horizontal,
  threshold = 0.8,
  handleScroll,
  loading,
  loadData,
  forceResize
}: Props): [boolean, React.Dispatch<React.SetStateAction<boolean>>] => {
  const [_loading, setLoading] = React.useState(false);

  const calculateThreshold = el => {
    if (horizontal) {
      if (!el.scrollWidth) return;
      const scrollPosition = el.clientWidth + el.scrollLeft;
      return scrollPosition / el.scrollWidth;
    } else {
      if (!el.scrollHeight) return;
      const scrollPosition = el.clientHeight + el.scrollTop;
      return scrollPosition / el.scrollHeight;
    }
  };

  const _handleScroll = debounce(() => {
    if (!container?.current) return;
    const t = calculateThreshold(container.current);
    if (!_loading && t >= threshold) {
      if (handleScroll) {
        handleScroll();
      } else if (loadData) {
        setLoading(true);
      }
    }
  }, 75);

  const handleResize = () => {
    const div = container?.current;
    if (!div) return;
    if (horizontal) {
      if (div.scrollWidth <= div.clientWidth) {
        _handleScroll();
      }
    } else {
      if (div.scrollHeight <= div.clientHeight) {
        _handleScroll();
      }
    }
  };

  React.useEffect(() => {
    handleResize();
  }, [forceResize]);

  React.useEffect(() => {
    if (!container?.current) return;
    container.current.addEventListener('scroll', _handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      container.current.removeEventListener('scroll', _handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [container, _loading]);

  React.useEffect(() => {
    if (!_loading || !loadData) return;
    loadData(() => setLoading(false));
  }, [_loading]);

  React.useEffect(() => {
    setLoading(loading);
  }, [loading]);

  return [_loading, setLoading];
};

export default InfiniteLoader;
